import { observable, action, makeObservable } from 'mobx';
import transactionApi from 'services/transactionApi';
import { Banknotes } from 'helpers/constants/Banknotes';
import { ReceiptGenerator } from '../../helpers/ReceiptGenerator';
import { StoreModel } from 'models/Index';
import { AdminModel } from '../admin';
import { BanknoteModel, ReceiptPrintResult } from '@b2bsoft/kiosk-shell-sdk';
import { UserCashMetrics } from 'interfaces';
import { LogEvents, logger } from 'helpers';

interface ObjectXReport {
    paymentTypeID?: number;
    isExternal?: boolean;
    paymentTypeName?: string;
    manualCount?: number;
    systemCount?: number;
    variance?: number;
    cashCount?: number;
    floatValue?: number;
    checksManualCount?: number;
}

interface IRegisterRequestModel {
    registerName?: string;
    locationId?: number;
    user?: string;
    systemCount?: number;
    manualCount?: number;
    variance?: number;
    denominationCountList?: Array<never>;
}

interface XReport {
    closingPays: Array<ObjectXReport>;
}

export default class CollectionModel {
    @observable public xReport?: XReport;
    @observable public cashAmount?: number;
    @observable public acceptorMetrics?: BanknoteModel;
    @observable public cashCollectionReport?: string;
    @observable public userCashMetrics?: UserCashMetrics;

    root: StoreModel;
    parent: AdminModel;

    constructor(root: StoreModel, parent: AdminModel) {
        this.root = root;
        this.parent = parent;
        makeObservable(this);
    }

    @action public setUserCashMetrics(userCashMetrics: UserCashMetrics) {
        this.userCashMetrics = userCashMetrics;
    }

    @action public cleanData = () => {
        this.xReport = undefined;
        this.cashAmount = undefined;
        this.acceptorMetrics = undefined;
        this.cashCollectionReport = undefined;
        this.userCashMetrics = undefined;
    };

    @action public submitReport = async () => {
        const { loader, batchId } = this.root;

        loader.show(
            'Thank you for your patience while we gather data and calculate metrics for you. This process may take 1 to 2 minutes to complete.'
        );

        let promises = [];

        try {
            promises.push(
                this.getXReport().then((result) => {
                    if (!result) throw Error('XReport has invalid data');

                    this.xReport = result;
                    console.log('XReport data: ', result);
                })
            );

            promises.push(this.getAcceptorCashCount());

            await Promise.all(promises);

            await this.closeRegister().then(() => this.openRegister());

            this.computeDiscrepancy();

            const clearMetricsPromise =
                this.root.shellClient.cashAcceptor.clearMetrics();
            const generateCashCollectionReportPromise =
                this.generateCashCollectionReport(batchId);

            await Promise.all([
                clearMetricsPromise,
                generateCashCollectionReportPromise,
            ]);
        } finally {
            loader.hide();
        }
    };

    public printCashCollectionReport = async () => {
        if (!this.cashCollectionReport) {
            console.log('CashCollectionReport not exists');
            return;
        }

        console.log('Printing CashCollectionReport');

        const printResult: ReceiptPrintResult =
            await this.root.shellClient.printReceipt(this.cashCollectionReport);

        if (!printResult.isSuccess) {
            throw Error(printResult?.errorMessage || 'Printing failed');
        }
    };

    public openRegister = async (
        registerName = this.root.registerName,
        locationId = this.root.locationId
    ) => {
        const openRegisterModel = this.registerRequestModel({
            registerName: registerName,
            locationId: locationId,
        });
        const openRegisterResult: number = await transactionApi.openRegister(
            openRegisterModel
        );

        await this.root.initShellValues({
            batchId: openRegisterResult.toString(),
        });
    };

    public getXReport = async (): Promise<XReport> => {
        const { locationId, registerName } = this.root;
        const response = await transactionApi.getXReport(
            locationId!,
            registerName
        );
        return response?.data;
    };

    @action private getAcceptorCashCount = async () => {
        console.log('Getting cash acceptor metrics');
        const cashInfo: BanknoteModel =
            await this.root.shellClient.cashAcceptor.getMetrics();
        console.log('Cash acceptor data: ', cashInfo);

        if (!cashInfo) throw Error('Cash information has invalid data');

        this.acceptorMetrics = cashInfo;
        let cashAmount = 0;

        Object.entries(cashInfo).forEach(([key, value]) => {
            if (Banknotes.hasOwnProperty(key))
                cashAmount += ((Banknotes as any)[key] as number) * value;
        });

        this.cashAmount = cashAmount;
        return cashInfo;
    };

    @action private generateCashCollectionReport = async (
        closedBatch: string
    ) => {
        this.cashCollectionReport =
            await ReceiptGenerator.generateCustomFailedCollectionReceipt(
                this.root,
                closedBatch,
                this.parent.userName
            );
    };

    private closeRegister = () => {
        const deviceCount = this.xReport?.closingPays[0].systemCount;
        const systemCount = Number(deviceCount?.toFixed(2));
        const manualCount = Number(this.cashAmount?.toFixed(2));
        const variance = Number(manualCount - systemCount);

        let denominationCountList = [] as any;

        if (!this.acceptorMetrics) {
            console.log('AcceptorMetrics not exists');
            return Promise.reject();
        }

        Object.entries(this.acceptorMetrics).forEach(([key, value]) => {
            if (!Banknotes.hasOwnProperty(key)) return;
            const banknoteCount = (Banknotes as any)[key] as number;
            const count = banknoteCount * value;
            const nominal = Banknotes[key as keyof typeof Banknotes];
            const type = 'D';
            const denominationId = 0;

            const denominationCount = {
                count,
                nominal,
                type,
                denominationId,
            };

            denominationCountList.push(denominationCount);
        });

        const closeRegisterRequest = this.registerRequestModel({
            systemCount,
            manualCount,
            variance,
            denominationCountList,
        });

        return transactionApi.closeRegister(closeRegisterRequest);
    };

    private registerRequestModel = ({
        registerName = this.root.registerName,
        locationId = this.root.locationId,
        user = this.root.user,
        systemCount = 0,
        manualCount = 0,
        variance = 0,
        denominationCountList = [],
    }: IRegisterRequestModel) => {
        return {
            cashRegisterInfo: {
                user,
                registerId: registerName,
                storeId: locationId,
                note: '',
                cashPickupType: '',
            },
            cashRegisterReconciliation: {
                registerCount: [
                    {
                        class: 'Payment',
                        type: 'CashPayment',
                        couponId: 0,
                        floatAdjustment: 0,
                        manualCount,
                        systemCount,
                        variance,
                        denominationCountList,
                    },
                ],
            },
        };
    };

    private computeDiscrepancy() {
        const deviceCount = this.xReport?.closingPays[0].systemCount;
        const systemCount = Number(deviceCount?.toFixed(2));
        let denominationDiscrepancy: { value: number; count: number }[] = [];
        let acceptorCount = 0;

        this.acceptorMetrics &&
            Object.entries(this.acceptorMetrics).forEach(([key, value]) => {
                if (!Banknotes.hasOwnProperty(key)) return;
                const denomination = (Banknotes as any)[key] as number;
                acceptorCount += value;
                const userCount =
                    this.userCashMetrics?.denominations.find(
                        (b) => b.denomination === denomination.toString()
                    )?.count || 0;
                const discrepancy = value - userCount;
                const discrepancyRecord = {
                    value: denomination,
                    count: discrepancy,
                };

                denominationDiscrepancy.push(discrepancyRecord);
            });

            const totalDiscrepancy = this.userCashMetrics?.totalAmount
                ? systemCount - this.userCashMetrics?.totalAmount
                : null;
            const logObject = {
                totalFromXReport: systemCount,
                totalValue: this.userCashMetrics?.totalAmount ?? 0,
                totalDiscrepancy: totalDiscrepancy,
                totalBillCountAcceptor: acceptorCount,
                totalBillCountCollection: this.userCashMetrics?.totalCount ?? 0,
                denominationDiscrepancy,
            };

            if (
                denominationDiscrepancy.some(
                    (d: { count: number }) => d.count !== 0
                )
            ) {
                logger.logError(
                    'Found Discrepancy after collection',
                    LogEvents.Discrepancy,
                    {
                        message: logObject,
                    }
                );
            } else {
                logger.logInformation(
                    'No Discrepancy after collection',
                    LogEvents.NoDiscrepancy,
                    {
                        message: logObject,
                    }
                );
            }
    }
}
